// Module 5 / Slice 5 — Rota Configuration list screen.
//
// Read-first paginated list of rota_config.rota rows; filters compose
// status (multi-select) + org_unit_id (typeahead from M3 ungated for
// now). Each row links to the detail screen via the TanStack Router
// /admin/rota-config/rotas/$rotaId route.
//
// Per MODULE_5_PLAN.md §10 the SPA stays behind the
// `rota.admin_ui.enabled` feature flag — wiring happens at the AppShell
// nav level (route remains reachable for power users).
//
// PATTERNS.md §2.2 (screen composition) + §2.5 (data-testid naming).

import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useState } from "react";
import { rotaConfigApiClient } from "../../api/client";
import { useI18n } from "../../i18n/I18nContext";

type RotaStatus = "draft" | "published" | "retired";

const STATUS_OPTIONS: readonly RotaStatus[] = ["draft", "published", "retired"];

export function RotaListScreen(): JSX.Element {
  const { t } = useI18n();
  const [statusFilter, setStatusFilter] = useState<RotaStatus | "">("");
  const [orgUnitId, setOrgUnitId] = useState<string>("");

  const query = useQuery({
    queryKey: ["rota-config", "rotas", { statusFilter, orgUnitId }] as const,
    queryFn: async () => {
      const response = await rotaConfigApiClient.GET("/rota-config/rotas", {
        params: {
          query: {
            limit: 200,
            includeArchived: false,
            ...(statusFilter !== "" ? { status: [statusFilter] } : {}),
            ...(orgUnitId.trim().length > 0 ? { orgUnitId: orgUnitId.trim() } : {}),
          },
        },
      });
      if (!response.data) {
        throw new Error("rota_rotas_fetch_failed");
      }
      return response.data;
    },
  });

  return (
    <div data-testid="rota-list-screen">
      <h1>{t("rota_config.rotas.heading")}</h1>
      <p className="rc-help">{t("rota_config.rotas.description")}</p>

      <div style={{ marginBottom: "1rem", display: "flex", gap: "1rem", flexWrap: "wrap" }}>
        <label htmlFor="rota-filter-status">
          {t("rota_config.rotas.filter.status")}{" "}
          <select
            id="rota-filter-status"
            data-testid="rota-filter-status"
            value={statusFilter}
            onChange={(e) => setStatusFilter((e.target.value as RotaStatus | "") ?? "")}
            style={{ marginLeft: "0.5rem" }}
          >
            <option value="">{t("rota_config.rotas.filter.status.all")}</option>
            {STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {t(`rota_config.rotas.filter.status.${s}`)}
              </option>
            ))}
          </select>
        </label>

        <label htmlFor="rota-filter-org-unit">
          {t("rota_config.rotas.filter.orgUnit")}{" "}
          <input
            id="rota-filter-org-unit"
            data-testid="rota-filter-org-unit"
            type="text"
            value={orgUnitId}
            placeholder={t("rota_config.rotas.filter.orgUnit.placeholder")}
            onChange={(e) => setOrgUnitId(e.target.value)}
            style={{ marginLeft: "0.5rem" }}
          />
        </label>
      </div>

      {query.isLoading && <p>{t("rota_config.rotas.loading")}</p>}
      {query.isError && (
        <p role="alert" data-testid="rota-list-error" style={{ color: "var(--rc-color-danger)" }}>
          {(query.error as Error).message}
        </p>
      )}
      {query.data && (query.data.items ?? []).length === 0 && (
        <p data-testid="rota-list-empty">{t("rota_config.rotas.empty")}</p>
      )}
      {query.data && (query.data.items ?? []).length > 0 && (
        <table className="rc-table" data-testid="rota-list-table">
          <thead>
            <tr>
              <th>{t("rota_config.rotas.col.name")}</th>
              <th>{t("rota_config.rotas.col.status")}</th>
              <th>{t("rota_config.rotas.col.orgUnit")}</th>
              <th>{t("rota_config.rotas.col.effectiveFrom")}</th>
              <th>{t("rota_config.rotas.col.effectiveTo")}</th>
            </tr>
          </thead>
          <tbody>
            {(query.data.items ?? []).map((row) => (
              <tr key={row.id} data-testid={`rota-row-${row.id}`}>
                <td>
                  <Link
                    to="/admin/rota-config/rotas/$rotaId"
                    params={{ rotaId: row.id }}
                    data-testid={`rota-link-${row.id}`}
                  >
                    {row.name}
                  </Link>
                </td>
                <td>
                  <span data-testid={`rota-status-${row.id}`}>{row.status}</span>
                </td>
                <td>{row.orgUnitId}</td>
                <td>{row.effectiveFrom ?? "—"}</td>
                <td>{row.effectiveTo ?? "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
